import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LifeBuoy, ShieldAlert } from "lucide-react";

import { enterWithToken, readEnterToken } from "@/lib/supportSession";

/**
 * Where a partner / Tray-support technician lands (ADR-0081 S3).
 *
 * Central sends them here with the signed grant in the URL FRAGMENT. This page spends it
 * once and gets out of the way — there is nothing to fill in, because the permission IS
 * the credential and asking for anything else would imply the visitor has an account.
 *
 * Two details are load-bearing:
 *
 * - the fragment is **wiped before the exchange**, not after, so a grant never survives a
 *   reload, a bookmark, or a screenshot of the address bar;
 * - a refusal is shown VERBATIM (`access:consent_off`, `access:code_mismatch`, …) because
 *   the technician needs to know whether to ask us for a new permission or ask the
 *   customer to switch consent on. A generic "access denied" just produces a support
 *   ticket that reaches the same answer more slowly.
 */
export function SupportEnterPage() {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  // StrictMode double-invokes effects in dev; a grant is SINGLE-USE-ish (it opens or
  // resumes one visit), so guard rather than spend it twice.
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;

    const token = readEnterToken(window.location.hash);
    window.history.replaceState(null, "", window.location.pathname);
    if (!token) {
      setError("Nessun permesso di accesso in questo link.");
      return;
    }
    enterWithToken(token)
      .then(() => navigate("/", { replace: true }))
      .catch((e: Error) => setError(e.message));
  }, [navigate]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-6">
      <div className="w-full max-w-md rounded-lg border border-border bg-card p-6 text-center">
        {error ? (
          <>
            <ShieldAlert className="mx-auto mb-3 h-8 w-8 text-destructive" />
            <h1 className="text-base font-semibold">Accesso rifiutato</h1>
            <p className="mt-2 break-words font-mono text-xs text-muted-foreground">{error}</p>
            <p className="mt-4 text-sm text-muted-foreground">
              Il cliente deve avere l&apos;accesso partner attivo e il tuo codice impostato
              nelle sue impostazioni.
            </p>
          </>
        ) : (
          <>
            <LifeBuoy className="mx-auto mb-3 h-8 w-8 animate-pulse text-muted-foreground" />
            <h1 className="text-base font-semibold">Accesso in corso…</h1>
            <p className="mt-2 text-sm text-muted-foreground">
              Verifica del permesso su questa installazione.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
